import { User, CreatorProfile } from '@/types';
import { supabase } from '../supabase/client';
import { authMonitor } from './monitoring';
import { traceAuthFlow } from './debug';
import { getAuthManager } from './get-auth-manager';

interface ProfileCache {
  user: User | null;
  creatorProfile: CreatorProfile | null;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

export class ProfileManager {
  private cache: ProfileCache = {
    user: null,
    creatorProfile: null,
    timestamp: 0,
    ttl: 5 * 60 * 1000 // 5 minutes
  };
  
  private loading = false;
  private error: Error | null = null;
  private listeners = new Set<(user: User | null) => void>();

  constructor() {
    // Initialization is handled in init-auth-system.ts to avoid circular dependencies
  }

  async getProfile(forceRefresh = false): Promise<{ user: User | null; error: Error | null }> {
    try {
      if (!forceRefresh && this.isCacheValid()) {
        traceAuthFlow('ProfileManager.getProfile', { source: 'cache' });
        return { user: this.cache.user, error: null };
      }

      const authManager = await getAuthManager();
      const session = authManager.getSession();
      if (!session) {
        return { user: null, error: null }; // Not an error, just not authenticated
      }

      authMonitor.startTimer('profileFetch');
      traceAuthFlow('ProfileManager.getProfile', { source: 'database', userId: session.user.id });
      
      this.loading = true;
      this.error = null;

      const { data: profile, error } = await supabase
        .from('members')
        .select('*')
        .eq('id', session.user.id)
        .single();

      if (error) {
        authMonitor.endTimer('profileFetch', false, error);
        this.error = error;
        this.loading = false;

        // Return cached data if available, even if stale
        if (this.cache.user) {
          console.warn('Profile fetch failed, using stale cache:', error);
          return { user: this.cache.user, error };
        }

        return { user: null, error };
      }

      const { data: creatorProfile } = await supabase
        .from('creator_profiles')
        .select('*')
        .eq('user_id', session.user.id)
        .maybeSingle();

      const profileWithCreator = {
        ...profile,
        creator_profile: creatorProfile || null
      };

      const mappedUser: User = {
        ...profileWithCreator,
        status: profile.status || 'active'
      };
      
      // Handle creator_profile which might be an array from the query
      if (profile.creator_profile) {
        if (Array.isArray(profile.creator_profile)) {
          mappedUser.creator_profile = profile.creator_profile.length > 0 ? profile.creator_profile[0] : null;
        } else {
          mappedUser.creator_profile = profile.creator_profile;
        }
      } else {
        mappedUser.creator_profile = null;
      }

      this.cache = {
        user: mappedUser,
        creatorProfile: mappedUser.creator_profile || null,
        timestamp: Date.now(),
        ttl: this.cache.ttl
      };

      this.loading = false;
      authMonitor.endTimer('profileFetch', true);
      
      this.notifyListeners(mappedUser);

      return { user: mappedUser, error: null };

    } catch (error) {
      authMonitor.endTimer('profileFetch', false, error as Error);
      this.error = error as Error;
      this.loading = false;
      
      if (this.cache.user) {
        return { user: this.cache.user, error: error as Error };
      }
      
      return { user: null, error: error as Error };
    }
  }

  async getRole(): Promise<'user' | 'creator' | 'admin' | null> {
    const { user } = await this.getProfile();
    return user?.role || null;
  }

  async updateProfile(updates: Partial<User>): Promise<{ user: User | null; error: Error | null }> {
    try {
      const authManager = await getAuthManager();
      const session = authManager.getSession();
      if (!session) {
        return { user: null, error: new Error('Not authenticated') };
      }

      traceAuthFlow('ProfileManager.updateProfile', { updates });

      const { data: updatedUser, error } = await supabase
        .from('members')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', session.user.id)
        .select('*')
        .single();

      if (error) {
        return { user: null, error };
      }

      const { data: creatorProfile } = await supabase
        .from('creator_profiles')
        .select('*')
        .eq('user_id', session.user.id)
        .maybeSingle();

      const _updatedUserWithCreator = {
        ...updatedUser,
        creator_profile: creatorProfile || null
      };

      this.clearCache();

      return this.getProfile(true);

    } catch (error) {
      return { user: null, error: error as Error };
    }
  }

  private isCacheValid(): boolean {
    if (!this.cache.user) return false;
    
    const age = Date.now() - this.cache.timestamp;
    return age < this.cache.ttl;
  }

  clearCache() {
    traceAuthFlow('ProfileManager.clearCache');
    this.cache = {
      user: null,
      creatorProfile: null,
      timestamp: 0,
      ttl: this.cache.ttl
    };
    this.notifyListeners(null);
  }

  isLoading(): boolean {
    return this.loading;
  }

  getError(): Error | null {
    return this.error;
  }

  subscribe(listener: (user: User | null) => void): () => void {
    this.listeners.add(listener);
    
    if (this.cache.user) {
      listener(this.cache.user);
    }
    
    return () => {
      this.listeners.delete(listener);
    };
  }

  setCacheTTL(ttl: number) {
    this.cache.ttl = ttl;
  }

  private notifyListeners(user: User | null) {
    this.listeners.forEach(listener => {
      try {
        listener(user);
      } catch (error) {
        console.error('Profile listener error:', error);
      }
    });
  }
}

export const profileManager = new ProfileManager();

if (typeof window !== 'undefined') {
  (window as any).profileManager = profileManager;
} 